/**
 * Deterministic synthetic telemetry data for the homepage observability dashboard.
 *
 * Every series here comes from a seeded PRNG (mulberry32) so server-rendered HTML
 * and client hydration produce byte-identical markup. Nothing in this module reads
 * the system clock or a global RNG — callers that need "now" must pass it in
 * explicitly (see `formatUptime`).
 */

/** mulberry32: small, fast, deterministic PRNG. Returns a `() => number` in [0, 1). */
export function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return function next() {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Maps a series of numbers to an SVG path string, scaled to fit `width` x `height`.
 * Always begins with an `M` (moveto) command followed by `points.length - 1` `L`
 * (lineto) commands.
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function sparklinePath(points: number[], width: number, height: number): string {
  if (points.length === 0) return "";

  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const stepX = points.length > 1 ? width / (points.length - 1) : 0;

  const coords = points.map((point, i) => ({
    x: i * stepX,
    y: height - ((point - min) / range) * height,
  }));

  if (coords.length < 3) {
    return `M ${coords.map((c) => `${c.x.toFixed(2)} ${c.y.toFixed(2)}`).join(" L ")}`;
  }

  // Catmull-Rom to cubic Bezier for a smooth curve through every point.
  let d = `M ${coords[0].x.toFixed(2)} ${coords[0].y.toFixed(2)}`;
  for (let i = 0; i < coords.length - 1; i++) {
    const p0 = coords[i - 1] ?? coords[i];
    const p1 = coords[i];
    const p2 = coords[i + 1];
    const p3 = coords[i + 2] ?? p2;

    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = clamp(p1.y + (p2.y - p0.y) / 6, 0, height);
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = clamp(p2.y - (p3.y - p1.y) / 6, 0, height);

    d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }

  return d;
}

/**
 * Formats the time elapsed between two ISO timestamps as `"Ny Nmo"`.
 * Takes `nowISO` as an explicit argument — it never reads the clock itself, so
 * it is safe to call from a Server Component and produces stable output for
 * any fixed pair of inputs.
 */
export function formatUptime(startISO: string, nowISO: string): string {
  const start = new Date(startISO);
  const now = new Date(nowISO);

  let months =
    (now.getUTCFullYear() - start.getUTCFullYear()) * 12 +
    (now.getUTCMonth() - start.getUTCMonth());

  if (now.getUTCDate() < start.getUTCDate()) {
    months -= 1;
  }
  if (months < 0) {
    months = 0;
  }

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  if (years === 0) return `${remainingMonths}mo`;
  return `${years}y ${remainingMonths}mo`;
}

/** Epoch for the live service-uptime clock in the header: 9AM MDT (UTC-6), May 31 2016. */
export const UPTIME_EPOCH_ISO = "2016-05-31T15:00:00Z";

/**
 * Formats elapsed time since `epochISO` as `"Ny Nd HH:MM"`, minute-precision.
 * Takes `nowMs` as an explicit argument so callers control the clock (e.g. a
 * client component ticking on a `setInterval`).
 */
export function formatLiveUptime(epochISO: string, nowMs: number): string {
  const epochMs = new Date(epochISO).getTime();
  const totalMinutes = Math.max(0, Math.floor((nowMs - epochMs) / 60000));

  const minutes = totalMinutes % 60;
  const totalHours = Math.floor(totalMinutes / 60);
  const hours = totalHours % 24;
  const totalDays = Math.floor(totalHours / 24);

  const years = Math.floor(totalDays / 365.25);
  const daysAfterYears = totalDays - years * 365.25;
  const months = Math.floor(daysAfterYears / 30.44);
  const days = Math.floor(daysAfterYears - months * 30.44);

  return `${years}y ${months}mo ${days}d ${hours}h ${minutes}m`;
}

export interface SeriesPoint {
  timestamp: string;
  value: number;
  /** Present only at a day boundary; drives the sparkline's axis tick. */
  dayLabel?: string;
}

export type WeeklySeries = SeriesPoint[];

export type SeriesTrend = "up" | "down" | "flat";

/** Compares a series' last value to its first, relative to its own range, to classify direction. */
export function seriesTrend(series: WeeklySeries): SeriesTrend {
  const first = series[0].value;
  const last = series[series.length - 1].value;
  const values = series.map((point) => point.value);
  const delta = last - first;
  const range = Math.max(...values) - Math.min(...values) || 1;
  if (Math.abs(delta) < range * 0.15) return "flat";
  return delta > 0 ? "up" : "down";
}

const DAY_LETTERS = ["S", "M", "T", "W", "T", "F", "S"];

/**
 * Wraps a plain `number[]` (one point per day, oldest -> newest, ending "now")
 * into a `WeeklySeries` with single-letter day-of-week labels. Never reads the
 * clock itself -- callers must pass `nowISO` explicitly (see `formatUptime`).
 */
export function buildDailyWeeklySeries(values: number[], nowISO: string): WeeklySeries {
  const now = new Date(nowISO);
  const offsetFromEnd = values.length - 1;

  return values.map((value, i) => {
    const day = new Date(now);
    day.setUTCDate(day.getUTCDate() - (offsetFromEnd - i));
    day.setUTCHours(0, 0, 0, 0);
    return {
      timestamp: day.toISOString(),
      value,
      dayLabel: DAY_LETTERS[day.getUTCDay()],
    };
  });
}

/**
 * Fills gaps in a sparse daily series (`null` where no real sample existed,
 * e.g. a day with no traffic) via linear interpolation between the nearest
 * known values on either side. Leading/trailing gaps are flat-filled from the
 * nearest known value. Returns all-zero if no value is known at all -- callers
 * should treat that upstream as "no data" before reaching this function.
 */
export function interpolateSparseDaily(values: Array<number | null>): number[] {
  const result: Array<number | null> = [...values];
  const knownIndices = result
    .map((v, i) => (v !== null && Number.isFinite(v) ? i : -1))
    .filter((i) => i !== -1);

  if (knownIndices.length === 0) return result.map(() => 0);

  const firstKnown = knownIndices[0];
  const lastKnown = knownIndices[knownIndices.length - 1];

  for (let i = 0; i < firstKnown; i++) result[i] = result[firstKnown];
  for (let i = lastKnown + 1; i < result.length; i++) result[i] = result[lastKnown];

  for (let k = 0; k < knownIndices.length - 1; k++) {
    const start = knownIndices[k];
    const end = knownIndices[k + 1];
    if (end - start <= 1) continue;
    const startVal = result[start] as number;
    const endVal = result[end] as number;
    for (let i = start + 1; i < end; i++) {
      const t = (i - start) / (end - start);
      result[i] = startVal + (endVal - startVal) * t;
    }
  }

  return result as number[];
}

export interface CaffeineDose {
  /** Hour of day (0-23) the dose is taken. */
  hour: number;
  /** Caffeine content of the dose, in mg (e.g. ~95mg for a brewed coffee). */
  mg: number;
  /** Probability [0,1] this dose happens on a given day. Defaults to 1 (always). */
  probability?: number;
  /** Extra probability added on Mondays, on top of `probability` (clamped to 1). */
  mondayBoost?: number;
}

export interface CaffeineSeriesOptions {
  doses: CaffeineDose[];
  /** Plasma elimination half-life, in hours. Defaults to 5 (typical for caffeine). */
  halfLifeHours?: number;
  /** Absorption half-life, in hours. Defaults to 0.75 (~45min to peak). */
  absorptionHalfLifeHours?: number;
  /** Apparent volume of distribution, in liters. Defaults to 36 (~0.5 L/kg for a 70kg adult). */
  volumeOfDistributionL?: number;
  min: number;
  max: number;
}

/**
 * One-compartment pharmacokinetic model (oral dose, first-order absorption
 * and elimination): plasma concentration in ng/mL at `hoursSinceDose` after
 * a single dose. Zero before the dose is taken.
 */
function plasmaConcentration(
  hoursSinceDose: number,
  doseMg: number,
  ka: number,
  ke: number,
  volumeOfDistributionL: number,
): number {
  if (hoursSinceDose < 0) return 0;
  const doseNg = doseMg * 1_000_000;
  const vdMl = volumeOfDistributionL * 1000;
  return (
    ((doseNg * ka) / (vdMl * (ka - ke))) *
    (Math.exp(-ke * hoursSinceDose) - Math.exp(-ka * hoursSinceDose))
  );
}

/**
 * Deterministic intra-day caffeine series across `days` days at
 * `samplesPerDay` resolution: each configured dose is taken on a seeded
 * per-day coin flip (boosted on Mondays), then decays via first-order
 * pharmacokinetics, with doses from prior days still contributing residual
 * concentration. Values are in ng/mL. Identical `seed` always produces the
 * identical array.
 */
export function seededCaffeineSeries(
  seed: number,
  days: number,
  samplesPerDay: number,
  opts: CaffeineSeriesOptions,
  nowISO: string,
): WeeklySeries {
  const {
    doses,
    halfLifeHours = 5,
    absorptionHalfLifeHours = 0.75,
    volumeOfDistributionL = 36,
    min,
    max,
  } = opts;
  const rand = mulberry32(seed);
  const ke = Math.LN2 / halfLifeHours;
  const ka = Math.LN2 / absorptionHalfLifeHours;
  const hoursPerSample = 24 / samplesPerDay;

  // Anchor day boundaries to real calendar midnight (like `buildDailyWeeklySeries`),
  // not to `nowISO`'s time-of-day -- otherwise intraday day-of-week labels drift
  // out of alignment with the 1-point-per-day tiles.
  const now = new Date(nowISO);
  const todayMidnight = new Date(now);
  todayMidnight.setUTCHours(0, 0, 0, 0);

  const dayOfWeekByDay: number[] = [];
  for (let day = 0; day < days; day++) {
    const dayStart = new Date(todayMidnight);
    dayStart.setUTCDate(dayStart.getUTCDate() - (days - 1 - day));
    dayOfWeekByDay.push(dayStart.getUTCDay());
  }

  const doseEvents: Array<{ absHour: number; mg: number }> = [];
  for (let day = 0; day < days; day++) {
    const isMonday = dayOfWeekByDay[day] === 1;
    for (const dose of doses) {
      const probability = Math.min(
        1,
        (dose.probability ?? 1) + (isMonday ? (dose.mondayBoost ?? 0) : 0),
      );
      if (rand() < probability) {
        doseEvents.push({ absHour: day * 24 + dose.hour, mg: dose.mg });
      }
    }
  }

  // Timeline start: midnight of the oldest of the `days` days. Absolute hour offsets
  // below are measured from here, so full historical days still land exactly on
  // `hoursPerSample` multiples -- only "today" (the last day) is cut short at `now`
  // instead of running to a fixed end-of-day hour, so the sparkline (and the caller's
  // headline value, taken from the series' last point) reflect the real current time
  // rather than freezing until the calendar date rolls over.
  const timelineStart = new Date(todayMidnight);
  timelineStart.setUTCDate(timelineStart.getUTCDate() - (days - 1));
  const elapsedHoursIntoToday = (now.getTime() - todayMidnight.getTime()) / 3_600_000;
  const totalHoursToNow = (days - 1) * 24 + elapsedHoursIntoToday;

  const hours: number[] = [];
  for (let h = 0; h <= totalHoursToNow + 1e-9; h += hoursPerSample) {
    hours.push(h);
  }
  if (hours.length === 0 || Math.abs(hours[hours.length - 1] - totalHoursToNow) > 1e-9) {
    hours.push(totalHoursToNow);
  }

  const totalSamples = hours.length;

  // Place one label per day at the same fractional position daily (1-point-per-day)
  // series use, so sparkline day labels line up edge-to-edge across all stat tiles
  // regardless of intraday sample density.
  const labelSampleIndexForDay = (day: number) =>
    days > 1 ? Math.round((day * (totalSamples - 1)) / (days - 1)) : 0;
  const labelSampleIndices = new Set(
    Array.from({ length: days }, (_, day) => labelSampleIndexForDay(day)),
  );

  const series: WeeklySeries = hours.map((absHour, i) => {
    let value = 0;
    for (const event of doseEvents) {
      value += plasmaConcentration(absHour - event.absHour, event.mg, ka, ke, volumeOfDistributionL);
    }
    const noise = value * (rand() - 0.5) * 0.08;
    value = Math.min(max, Math.max(min, value + noise));

    const isLive = i === hours.length - 1;
    const timestamp = isLive ? now : new Date(timelineStart);
    if (!isLive) timestamp.setUTCHours(timestamp.getUTCHours() + absHour);

    return {
      timestamp: timestamp.toISOString(),
      value,
      dayLabel: labelSampleIndices.has(i) ? DAY_LETTERS[timestamp.getUTCDay()] : undefined,
    };
  });

  return series;
}

/** The 2016 -> present axis the career trace waterfall renders against. */
export const CAREER_AXIS_START_YEAR = 2016;

/**
 * One year past `now`, so the current (open) span — which always fills to
 * the axis's right edge — never snaps to a boundary that's already in the
 * past. Derived from the caller-supplied `now` rather than the system clock
 * so this module stays deterministic (see file header).
 */
export function careerAxisEndYear(now: Date): number {
  return now.getUTCFullYear() + 1;
}

function axisPct(year: number, endYear: number): number {
  return ((year - CAREER_AXIS_START_YEAR) / (endYear - CAREER_AXIS_START_YEAR)) * 100;
}

/** `startMonth`/`endMonth` are 0-indexed (Jan = 0), so partial years render as "1y 3mo" instead of rounding down. */
function closedDurationLabel(startYear: number, startMonth: number, endYear: number, endMonth: number): string {
  const totalMonths = (endYear - startYear) * 12 + (endMonth - startMonth);
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  if (years === 0) return `${months}mo`;
  if (months === 0) return years === 1 ? "1y" : `${years}y`;
  return `${years}y ${months}mo`;
}

export interface CareerSpan {
  id: string;
  employer: string;
  role: string;
  startYear: number;
  /** `null` marks the current, still-open role. */
  endYear: number | null;
  startISO: string;
  current: boolean;
  offsetPct: number;
  widthPct: number;
  /** Precomputed for closed spans; `null` for the current span (caller supplies via `formatUptime`). */
  durationLabel: string | null;
}

/** Builds the career trace spans against an axis ending one year past `now`. */
export function buildCareerSpans(now: Date): CareerSpan[] {
  const endYear = careerAxisEndYear(now);

  return [
    {
      id: "hp-intern",
      employer: "HP Inc.",
      role: "Software Intern",
      startYear: 2016,
      endYear: 2018,
      startISO: "2016-05-01",
      current: false,
      // Ran May 2016 -> May 2018 (per resume), placed on fractional-year
      // offsets like the HPE intern span so it doesn't snap to the
      // surrounding Jan 1 year boundaries.
      offsetPct: axisPct(2016 + 4 / 12, endYear),
      widthPct: axisPct(2018 + 4 / 12, endYear) - axisPct(2016 + 4 / 12, endYear),
      durationLabel: closedDurationLabel(2016, 4, 2018, 4),
    },
    {
      id: "hpe-intern",
      employer: "Hewlett Packard Enterprise",
      role: "Software Intern",
      // Displayed as a single year ("2018"); the underlying May–Aug 2018 stint is
      // too short to place on a year-granularity axis without a fractional offset.
      startYear: 2018,
      endYear: 2018,
      startISO: "2018-05-01",
      current: false,
      offsetPct: axisPct(2018 + 4 / 12, endYear),
      widthPct: axisPct(2018 + 7 / 12, endYear) - axisPct(2018 + 4 / 12, endYear),
      durationLabel: "3mo",
    },
    {
      id: "hpe-junior-swe",
      employer: "Hewlett Packard Enterprise",
      role: "Junior Software Engineer",
      startYear: 2019,
      endYear: 2020,
      startISO: "2019-01-01",
      current: false,
      // Ran January 2019 -> April 2020 (per resume); ends flush against the
      // OpenText span's real April 2020 start rather than snapping to Jan 1.
      offsetPct: axisPct(2019, endYear),
      widthPct: axisPct(2020 + 3 / 12, endYear) - axisPct(2019, endYear),
      durationLabel: closedDurationLabel(2019, 0, 2020, 3),
    },
    {
      id: "opentext-swe",
      employer: "OpenText",
      role: "Software Engineer",
      startYear: 2020,
      endYear: 2022,
      startISO: "2020-04-01",
      current: false,
      // Ran April 2020 -> June 2022 (per resume).
      offsetPct: axisPct(2020 + 3 / 12, endYear),
      widthPct: axisPct(2022 + 5 / 12, endYear) - axisPct(2020 + 3 / 12, endYear),
      durationLabel: closedDurationLabel(2020, 3, 2022, 5),
    },
    {
      id: "opentext-frontend-lead",
      employer: "OpenText",
      role: "Senior Software Engineer (Frontend Lead)",
      startYear: 2022,
      endYear: 2025,
      startISO: "2022-06-01",
      current: false,
      // Ran June 2022 -> ~September 2025 (per resume), before moving to the
      // UI Foundation platform team.
      offsetPct: axisPct(2022 + 5 / 12, endYear),
      widthPct: axisPct(2025 + 8 / 12, endYear) - axisPct(2022 + 5 / 12, endYear),
      durationLabel: closedDurationLabel(2022, 5, 2025, 8),
    },
    {
      id: "opentext-ui-foundation",
      employer: "OpenText",
      role: "Senior Software Engineer (UI Foundation)",
      startYear: 2025,
      endYear: null,
      startISO: "2025-10-01",
      current: true,
      // Started ~October 2025 (per resume), flush against the prior span's end.
      offsetPct: axisPct(2025 + 8 / 12, endYear),
      widthPct: axisPct(endYear, endYear) - axisPct(2025 + 8 / 12, endYear),
      durationLabel: null,
    },
  ];
}

export interface EmployerGroup {
  employer: string;
  offsetPct: number;
  widthPct: number;
  current: boolean;
  spans: CareerSpan[];
}

/**
 * Groups consecutive `CareerSpan`s under the same employer into a single
 * parent span (e.g. two HPE roles, or two OpenText roles), the way a
 * distributed trace nests child spans under the service span that owns them.
 * Assumes spans already appear in chronological, employer-contiguous order.
 */
export function groupCareerSpansByEmployer(spans: CareerSpan[]): EmployerGroup[] {
  const groups: EmployerGroup[] = [];

  for (const span of spans) {
    const last = groups[groups.length - 1];
    if (last && last.employer === span.employer) {
      last.spans.push(span);
    } else {
      groups.push({ employer: span.employer, offsetPct: 0, widthPct: 0, current: false, spans: [span] });
    }
  }

  for (const group of groups) {
    const starts = group.spans.map((span) => span.offsetPct);
    const ends = group.spans.map((span) => span.offsetPct + span.widthPct);
    const offsetPct = Math.min(...starts);
    group.offsetPct = offsetPct;
    group.widthPct = Math.max(...ends) - offsetPct;
    group.current = group.spans.some((span) => span.current);
  }

  return groups;
}

export function buildCareerGroups(now: Date): EmployerGroup[] {
  return groupCareerSpansByEmployer(buildCareerSpans(now));
}

export interface SkillGroup {
  id: string;
  label: string;
  technologies: string[];
}

export const skillGroups: SkillGroup[] = [
  {
    id: "frontend",
    label: "frontend/",
    technologies: ["React", "Next.js", "Angular", "TypeScript", "RxJS", "Microfrontends"],
  },
  {
    id: "backend",
    label: "backend/",
    technologies: ["Node.js", "Python", "Java", "Go", "C/C++"],
  },
  {
    id: "devops",
    label: "devops/",
    technologies: ["Docker", "Kubernetes", "Helm", "OpenTelemetry", "Nx", "GitLab CI"],
  },
  {
    id: "cloud",
    label: "cloud/",
    technologies: ["AWS", "Azure", "GCP", "PostgreSQL", "Elasticsearch", "Redis"],
  },
];
